import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { useT } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { CheckCircle2, XCircle, Loader2, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import ReferralCard from '@/components/ReferralCard';
import { useCheckoutPolling, MAX_ATTEMPTS } from '@/lib/useCheckoutPolling';

const LABELS = {
  fr: {
    checking: 'Confirmation du paiement...',
    success: 'Paiement réussi !',
    expired: 'Session expirée',
    error: 'Impossible de confirmer',
    successDesc: 'Bienvenue dans la team Créateur. Tu peux maintenant uploader sans limite et exporter en HD sans filigrane.',
    expiredDesc: "La session Stripe a expiré. Tu peux réessayer depuis la page de tarifs.",
    errorDesc: "Nous n'avons pas pu confirmer ton paiement. Si l'argent a été débité, contacte le support — sinon réessaie.",
    go: 'Aller au tableau de bord',
    retry: 'Réessayer',
  },
  en: {
    checking: 'Confirming your payment...',
    success: 'Payment successful!',
    expired: 'Session expired',
    error: 'Unable to confirm',
    successDesc: 'Welcome to Creator. You can now upload without limits and export in HD without watermark.',
    expiredDesc: 'The Stripe session expired. You can retry from the pricing page.',
    errorDesc: 'We could not confirm your payment. If you were charged, contact support — otherwise try again.',
    go: 'Go to dashboard',
    retry: 'Try again',
  },
};

const CheckingCard = ({ labels, attempts }) => (
  <>
    <Loader2 className="w-12 h-12 text-[#D4FF00] mx-auto mb-4 animate-spin" />
    <h1 className="font-heading text-2xl font-black mb-2">{labels.checking}</h1>
    <p className="text-zinc-500 text-sm">{attempts}/{MAX_ATTEMPTS}</p>
  </>
);

const SuccessCard = ({ labels, onGo }) => (
  <>
    <div className="w-20 h-20 rounded-full bg-[#D4FF00] mx-auto flex items-center justify-center mb-4 glow-accent">
      <CheckCircle2 className="w-10 h-10 text-black" strokeWidth={2.5} />
    </div>
    <h1 className="font-heading text-3xl font-black mb-2">{labels.success}</h1>
    <p className="text-zinc-400 text-sm mb-6">{labels.successDesc}</p>
    <Button onClick={onGo} className="bg-[#D4FF00] text-black hover:bg-[#B3D600] font-semibold w-full" data-testid="billing-success-cta">
      <Sparkles className="w-4 h-4 mr-2" /> {labels.go}
    </Button>
  </>
);

const FailureCard = ({ labels, isExpired, onRetry }) => (
  <>
    <XCircle className="w-14 h-14 text-zinc-500 mx-auto mb-4" />
    <h1 className="font-heading text-2xl font-black mb-2">{isExpired ? labels.expired : labels.error}</h1>
    <p className="text-zinc-400 text-sm mb-6">{isExpired ? labels.expiredDesc : labels.errorDesc}</p>
    <Button onClick={onRetry} variant="outline" className="border-white/15 w-full" data-testid="billing-retry-btn">{labels.retry}</Button>
  </>
);

const BillingSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const { lang } = useT();
  const labels = LABELS[lang === 'fr' ? 'fr' : 'en'];

  const refreshUser = React.useCallback(async () => {
    const me = await api.get('/auth/me');
    setUser(me.data);
  }, [setUser]);

  const { status, attempts } = useCheckoutPolling(sessionId, refreshUser);

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 dot-grid py-10" data-testid="billing-success-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-xl space-y-6">
        <div className="p-10 rounded-3xl border border-white/10 bg-black/60 backdrop-blur-xl text-center">
          {status === 'checking' && <CheckingCard labels={labels} attempts={attempts} />}
          {status === 'success' && <SuccessCard labels={labels} onGo={() => navigate('/dashboard')} />}
          {(status === 'expired' || status === 'error') && (
            <FailureCard labels={labels} isExpired={status === 'expired'} onRetry={() => navigate('/pricing')} />
          )}
        </div>

        {status === 'success' && <ReferralCard />}
      </motion.div>
    </div>
  );
};

export default BillingSuccess;
